import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';

import Button from '@components/common/Button';
import Select from '@components/common/Select';

import { Icons } from '@icons/icons';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IMultiDataFilter } from '@interfaces/components/table';

import styles from './MultiDataFilterForm.module.scss';

interface IMultiDataFilterParams {
	onClose?: () => void;
	containerClassName?: string;
	isVisibleForm: boolean;
	onClick: (option: TSelectOption<string>, subOption: TSelectOption<string>) => void;
	option: TSelectOption<string>;
	subOption: TSelectOption<string>;
	position?: 'top' | 'bottom';
	filterProps: IMultiDataFilter;
}

const POSITION_OFFSET = 5;

const MultiDataFilterForm = ({
	onClose,
	containerClassName,
	isVisibleForm,
	option,
	subOption,
	onClick,
	position = 'bottom',
	filterProps,
}: IMultiDataFilterParams) => {
	const [selectedOption, setSelectedOption] = useState<TSelectOption<string>>(option);
	const [selectedSubOption, setSelectedSubOption] = useState<TSelectOption<string>>(subOption);

	const {
		maxMenuHeight,
		maxWidth,
		minWidth,
		placeholder,
		subPlaceholder,
		name,
		subName,
		options,
		subOptions,
		width,
		getSubOptions,
	} = filterProps;

	const containerRef = useRef<HTMLDivElement | null>(null);

	const [containerHeight, setContainerHeight] = useState(0);

	const menuPositionStyle = position === 'top'
		? { top: `-${containerHeight}px` }
		: { bottom: `-${containerHeight}px` };

	const onContainerClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const onOptionChange = (option: TSelectOption<string>) => {
		setSelectedOption(option);

		if (option?.value && getSubOptions) {
			getSubOptions?.(option?.value);
		}
	};

	const onSubOptionChange = (subOption: TSelectOption<string>) => {
		setSelectedSubOption(subOption);
	};

	const onSubmit = () => {
		onClick(selectedOption, selectedSubOption);

		if (onClose) {
			onClose();
		}
	};

	useLayoutEffect(() => {
		if (containerRef.current && isVisibleForm) {
			setContainerHeight(containerRef.current.offsetHeight + POSITION_OFFSET);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef.current, isVisibleForm]);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => onClose && onClose(),
		fireEvent: 'mousedown',
		disableClickOutside: !isVisibleForm,
	});

	useEffect(() => {
		setSelectedOption(option);
		setSelectedSubOption(subOption);
	}, [option, subOption]);

	useEffect(() => {
		setSelectedSubOption(null);
	}, [selectedOption?.value]);

	const currentOptions = !options.length && option ? [option] : options;
	const currentSubOptions = !subOptions.length && subOption ? [subOption] : subOptions;

	const isSubmitDisabled = !selectedOption?.value || !selectedSubOption?.value;

	return (
		<div
			ref={containerRef}
			className={cn(containerClassName, styles.container)}
			onMouseDown={onContainerClick}
			style={menuPositionStyle}
		>
			{isVisibleForm && (
				<div className={cn(styles.form)}>
					<div className={styles.selectContainer}>
						<Select
							handleChange={onOptionChange}
							isClearable
							isFilterStyle
							isWithoutUseForms
							maxMenuHeight={maxMenuHeight}
							maxWidth={maxWidth}
							minWidth={minWidth}
							name={name}
							options={currentOptions}
							placeholder={placeholder}
							value={selectedOption}
							width={width}
						/>
						{Icons.link()}
						<Select
							disabled={!selectedOption}
							handleChange={onSubOptionChange}
							isClearable
							isFilterStyle
							isWithoutUseForms
							maxMenuHeight={maxMenuHeight}
							maxWidth={maxWidth}
							minWidth={minWidth}
							name={subName}
							options={currentSubOptions}
							placeholder={subPlaceholder}
							value={selectedSubOption}
							width={width}
						/>
					</div>
					<Button
						containerClassName={styles.button}
						disabled={isSubmitDisabled}
						onClick={onSubmit}
						text='Proceed'
					/>
				</div>
			)}
		</div>
	);
};

export default MultiDataFilterForm;
